@if (inline()) {
  <div class="masterdata-search-inline">
    <div class="masterdata-search-inline-header">
      <ng-container [ngTemplateOutlet]="modalHeader" />
    </div>
    <div class="masterdata-search-inline-body">
      <ng-container [ngTemplateOutlet]="modalBody" />
    </div>
    <div class="masterdata-search-inline-footer">
      <ng-container [ngTemplateOutlet]="modalFooter" />
    </div>
  </div>
}

@if (!inline() && isRow()) {
  <soft-ds-field-row
    [icon]="icon()"
    [label]="label() || null"
    [value]="rowValue()"
    [subvalue]="rowSubvalue()"
    [placeholder]="placeholder()"
    [required]="required()"
    [readonly]="readonly()"
    [pending]="loading()"
    [invalid]="isInvalid()"
    [errors]="errors()"
    (activate)="openExpanded()"
  />
}

@if (!inline()) {
  @if (!isRow() && !simplified() && showLabel() && label()) {
    <label soft-ds-label for="masterdata-search-input" [required]="required()">
      {{ label() }}
    </label>
  }

  @if (!isRow()) {
    <div class="masterdata-search-trigger">
      <soft-ds-search-input
        #searchInput
        cdkOverlayOrigin
        #trigger="cdkOverlayOrigin"
        inputId="masterdata-search-input"
        [size]="size()"
        class="masterdata-search-trigger-input"
        [class.is-selected]="!!selectedObject()"
        [class.is-multi-selected]="hasPicks()"
        [value]="triggerValue()"
        [icon]="triggerIcon()"
        [readonly]="readonly()"
        [invalid]="isInvalid()"
        [errors]="errors()"
        [trailingIcon]="triggerTrailingIcon()"
        trailingIconLabel="Erweiterte Suche öffnen"
        [loading]="triggerLoading()"
        loadingPlacement="trailing"
        [placeholder]="hasPicks() ? '' : placeholder()"
        (valueChange)="onTriggerInput($event)"
        (cleared)="onTriggerClear()"
        (trailingIconClick)="openExpanded()"
        (focusin)="setFocused()"
        (focusout)="onTriggerFocusout($event)"
        (click)="setFocused()"
        (keydown.arrowdown)="onArrowDown($event)"
        (keydown.arrowup)="onArrowUp($event)"
        (keydown.enter)="onEnterSelect($event)"
        (keydown.tab)="onTabSelect($event)"
        (keydown.delete)="onDeleteSelection($event)"
      >
        @if (hasPicks()) {
          <soft-ds-chip
            leading
            variant="primary"
            [removable]="!readonly()"
            removeLabel="Auswahl entfernen"
            (remove)="clearPicks()"
            size="sm"
          >
            {{ picksChipLabel() }}
          </soft-ds-chip>
        }

        @if (legacySlots()) {
          <div trailing class="masterdata-search-slots">
            @if (legacyHit(); as hit) {
              <span
                class="masterdata-search-hit"
                [class.is-direct]="hit.direct"
                data-testid="masterdata-hit-chip"
                aria-live="polite"
              >
                @if (hit.direct) {
                  <soft-ds-icon name="fa-regular fa-check" />
                  <span class="masterdata-search-hit-action">Enter übernimmt</span>
                } @else {
                  {{ hit.count }} Treffer
                }
              </span>
            }
            @if (showSpecialQueryKey()) {
              <button
                type="button"
                class="masterdata-search-slot"
                title="F3 – Spezialabfrage"
                data-testid="masterdata-slot-f3"
                (mousedown)="onSlotMousedown($event)"
                (click)="openSpecialQuery($event)"
              >
                <soft-ds-kbd [color]="searchFocused() ? 'success' : 'default'">F3</soft-ds-kbd>
              </button>
            }
            <button
              type="button"
              class="masterdata-search-slot"
              title="F4 – Erweiterte Suche"
              data-testid="masterdata-slot-f4"
              (mousedown)="onSlotMousedown($event)"
              (click)="openExpanded()"
            >
              <soft-ds-kbd [color]="searchFocused() ? 'brand' : 'default'">F4</soft-ds-kbd>
            </button>
          </div>
        } @else if (showSpecialQueryKey() && !readonly()) {
          <button
            trailing
            type="button"
            class="masterdata-search-slot masterdata-search-slot-icon"
            title="Spezialabfrage (F3)"
            data-testid="masterdata-slot-f3"
            (mousedown)="onSlotMousedown($event)"
            (click)="openSpecialQuery($event)"
          >
            <soft-ds-icon name="fa-regular fa-bars-filter" />
          </button>
        }
      </soft-ds-search-input>

      @if (showF4Hint() && !selectedObject() && !triggerLoading()) {
        <soft-ds-kbd class="masterdata-search-kbd" aria-hidden="true">F4</soft-ds-kbd>
      }

      @if (searchFocused() && simplified() && sortedLastSearchResults().length > 0) {
        <div class="flex flex-wrap gap-1 items-center mt-1">
          <p class="text-xs text-light">Letzte Suchanfragen:</p>
          @for (e of sortedLastSearchResults(); track e.key) {
            <soft-chip (click)="_value.set(e.body.text); search()" class="cursor-pointer">
              <i class="fa-regular fa-clock text-xs text-light"></i>
              {{ e.body.text }}
              <i
                class="fa-regular fa-xmark ml-1 text-xs text-light hover:text-color cursor-pointer"
                (click)="deleteLastSearchResult(e.key); $event.stopPropagation()"
              ></i>
            </soft-chip>
          }
        </div>
      }
    </div>
  }

  <soft-ds-overlay
    [originElement]="triggerEl()"
    [open]="isDropdownOpen()"
    (openChange)="onOverlayOpenChange($event)"
    [(expanded)]="panelExpanded"
    [mode]="overlayMode()"
    [hideExpandToggle]="true"
    [header]="panelExpanded() ? modalHeader : null"
    [content]="panelBody"
    [expandedContent]="mobileSheet() ? panelBody : modalBody"
    [footer]="panelExpanded() ? modalFooter : dropdownFooter"
    [panelClass]="overlaySheetClass()"
    [minWidth]="600"
    [width]="overlayWidth()"
    [maxWidth]="overlayMaxWidth()"
    [maxHeight]="dropdownMaxHeight()"
    [expandedWidth]="expandedWidthValue()"
    [expandedMaxHeight]="expandedMaxHeightValue()"
    [offset]="4"
    [dividers]="true"
    [trapFocus]="!panelExpanded()"
    [autoCaptureFocus]="false"
    (scroll)="onResultsScroll($event)"
  />
}

<ng-template #dropdownFooter>
  <div class="masterdata-search-footer">
    <soft-ds-button
      variant="outline"
      size="sm"
      class="masterdata-search-footer-expand"
      (click)="panelExpanded.set(true)"
    >
      <soft-ds-icon
        *softSlot="'leading'"
        name="fa-regular fa-sliders"
        size="sm"
        color="current"
      />
      Erweiterte Suche öffnen
    </soft-ds-button>
    <div class="masterdata-search-footer-hints">
      <span class="masterdata-search-footer-hint">
        <soft-ds-kbd aria-hidden="true">↑</soft-ds-kbd>
        <soft-ds-kbd aria-hidden="true">↓</soft-ds-kbd>
        <span class="masterdata-search-footer-hint-label">Navigieren</span>
      </span>
      <span class="masterdata-search-footer-hint">
        <soft-ds-kbd aria-hidden="true">enter</soft-ds-kbd>
        <span class="masterdata-search-footer-hint-label">Auswählen</span>
      </span>
      <span class="masterdata-search-footer-hint">
        <soft-ds-kbd aria-hidden="true">esc</soft-ds-kbd>
        <span class="masterdata-search-footer-hint-label">Schließen</span>
      </span>
    </div>
  </div>
</ng-template>

<ng-template #panelBody>
  @if (!_value() && sortedLastSearchResults().length > 0) {
    <soft-ds-select-section label="Letzte Suchanfragen" [sticky]="true">
      <soft-ds-select-section-action>
        <button
          type="button"
          class="masterdata-search-section-action ds-caption"
          (click)="deleteAllLastSearchResults()"
        >
          Alle löschen
        </button>
      </soft-ds-select-section-action>
      <div class="masterdata-search-chips">
        @for (e of sortedLastSearchResults(); track e.key) {
          <soft-ds-chip
            variant="outline"
            class="masterdata-search-chip"
            leadingIcon="fa-regular fa-clock"
            removable
            removeLabel="Suchanfrage entfernen"
            (click)="_value.set(e.body.text); search()"
            (remove)="deleteLastSearchResult(e.key)"
          >
            {{ e.body.text }}
          </soft-ds-chip>
        }
      </div>
    </soft-ds-select-section>
  }

  @if (!_value() && favoriteEntities().length > 0) {
    <soft-ds-select-section label="Favoriten" [sticky]="true">
      <div class="masterdata-search-list">
        @for (e of favoriteEntities(); track entityTrack(e); let i = $index) {
          <ng-container
            *ngTemplateOutlet="
              entityRow;
              context: {
                $implicit: e,
                onSelect: selectFromHistory,
                index: favoritesIndexOffset() + i,
              }
            "
          />
        }
      </div>
    </soft-ds-select-section>
  }

  @if (!_value() && sortedLastUsedEntities().length > 0) {
    <soft-ds-select-section label="Zuletzt verwendet" [sticky]="true">
      <soft-ds-select-section-action>
        <button
          type="button"
          class="masterdata-search-section-action ds-caption"
          (click)="clearLastUsed()"
        >
          Alle löschen
        </button>
      </soft-ds-select-section-action>
      <div class="masterdata-search-list">
        @for (e of sortedLastUsedEntities(); track entityTrack(e); let i = $index) {
          <ng-container
            *ngTemplateOutlet="
              entityRow;
              context: {
                $implicit: e,
                onSelect: selectFromHistory,
                index: lastUsedIndexOffset() + i,
              }
            "
          />
        }
      </div>
    </soft-ds-select-section>
  }

  @if (searching() && navigatingResults()) {
    <div class="masterdata-search-list masterdata-search-skeleton">
      <div class="masterdata-search-skeleton-row" *softRepeat="5">
        <soft-ds-loading-skeleton
          width="32px"
          height="32px"
          radius="var(--ds-radius-sm)"
        />
        <div class="masterdata-search-skeleton-lines">
          <soft-ds-loading-skeleton width="55%" height="14px" radius="4px" />
          <soft-ds-loading-skeleton width="35%" height="12px" radius="4px" />
        </div>
      </div>
    </div>
  } @else if (hasError()) {
    <soft-ds-empty-state
      class="masterdata-search-empty"
      variant="error"
      size="sm"
      [title]="errorTitle()"
      [description]="errorMessage()"
      actionLabel="Erneut versuchen"
      (action)="retry.emit()"
    />
  } @else if (
    (searchResults()?.length ?? 0) > 0 &&
    (_value() ? searched() : suggestions() || !hasHistory())
  ) {
    <soft-ds-select-section [label]="resultsSectionLabel()" [sticky]="true">
      <soft-ds-select-section-action>
        <soft-ds-badge variant="outline" size="sm">
          {{ resultCountLabel() }}
        </soft-ds-badge>
      </soft-ds-select-section-action>
      <div
        class="masterdata-search-list masterdata-search-list--results"
        #resultsListElement
      >
        @for (
          result of visibleSearchResults();
          track entityTrack(result);
          let i = $index
        ) {
          <ng-container
            *ngTemplateOutlet="
              entityRow;
              context: { $implicit: result, onSelect: selectResult, index: i }
            "
          />
        }
        @if (loadingMore()) {
          <div class="masterdata-search-list__more">
            <soft-ds-loading-spinner size="sm" />
          </div>
        }
      </div>
    </soft-ds-select-section>
  } @else if (!_value() && !hasHistory()) {
    @if (searched()) {
      <soft-ds-empty-state
        class="masterdata-search-empty"
        size="sm"
        icon="fa-regular fa-inbox"
        title="Keine Ergebnisse gefunden"
        description="Keine Einträge entsprechen den Suchkriterien."
      />
    } @else {
      <soft-ds-empty-state
        class="masterdata-search-empty"
        size="sm"
        icon="fa-regular fa-magnifying-glass"
        title="Suchbegriff eingeben"
        [description]="placeholder()"
      />
    }
  } @else if (_value()) {
    <soft-ds-empty-state
      class="masterdata-search-empty"
      size="sm"
      icon="fa-regular fa-inbox"
      title="Keine Ergebnisse gefunden"
      description="Keine Einträge entsprechen den Suchkriterien."
      actionLabel="Suche zurücksetzen"
      (action)="resetSearch()"
    />
  }
</ng-template>

<ng-template #modalHeader>
  <div class="masterdata-search-modal-header">
    @if (!inline()) {
      <div class="flex flex-row items-center justify-between gap-3">
        <div class="flex min-w-0 items-start gap-3">
          @if (icon(); as headerIcon) {
            <div
              class="masterdata-search-modal-title-icon flex shrink-0 items-center justify-center border bg-surface-secondary shadow-sm"
              [class.is-compact]="!modalSubtitle()"
            >
              <i [class]="headerIcon"></i>
            </div>
          }
          <div class="min-w-0">
            <h4 [class]="modalSubtitle() ? 'ds-body-1-semibold' : 'ds-title-1-semibold'">
              {{ modalTitle() }}
            </h4>
            @if (modalSubtitle(); as subtitle) {
              <h6 class="masterdata-search-modal-subtitle ds-caption color-light">
                {{ subtitle }}
              </h6>
            }
          </div>
        </div>
        <button
          type="button"
          aria-label="Schließen"
          class="masterdata-search-modal-close"
          (click)="dismissModal()"
        >
          <i class="fa-regular fa-xmark font-semibold"></i>
        </button>
      </div>
    }

    @if (showAdvancedFilters() && !mobileSheet()) {
      <div class="masterdata-search-modal-filters">
        <div class="masterdata-search-modal-filter">
          <label soft-ds-label for="masterdata-search-type">{{ typeLabel() }}</label>
          @if (typeOptions().length > 0) {
            <soft-ds-select
              size="md"
              searchable
              leadingIcon="fa-regular fa-users"
              [listboxTitle]="typeLabel()"
              [optionsCount]="typeOptionsCount()"
              [value]="typeFilter()"
              (valueChange)="typeFilter.set($event ?? null)"
              placeholder="Auswählen…"
            >
              @for (opt of typeOptions(); track opt.value) {
                <soft-ds-select-option [value]="opt.value" [label]="opt.label" />
              }
            </soft-ds-select>
          } @else {
            <soft-ds-input
              inputId="masterdata-search-type"
              placeholder="Placeholder"
              [value]="typeFilter()"
              (valueChange)="typeFilter.set($event)"
            />
          }
        </div>
        @if (showNumber()) {
          <div class="masterdata-search-modal-filter">
            <label soft-ds-label for="masterdata-search-number">
              {{ numberLabel() }}
            </label>
            <soft-ds-search-input
              inputId="masterdata-search-number"
              size="md"
              icon="fa-regular fa-hashtag"
              [placeholder]="numberLabel()"
              [value]="numberFilter()"
              (valueChange)="onNumberInput($event)"
            />
          </div>
        }
        @if (showCityFilter()) {
          <div class="masterdata-search-modal-filter">
            <label soft-ds-label for="masterdata-search-city">{{ cityLabel() }}</label>
            <soft-ds-multi-select
              size="md"
              searchable
              filterMode="external"
              [showSelectionCount]="true"
              clearable
              [showSelectAll]="true"
              selectAllLabel="Alle auswählen"
              leadingIcon="fa-regular fa-location-dot"
              [listboxTitle]="cityLabel()"
              [optionsCount]="cityOptionsCount()"
              [items]="cityOptions()"
              [hasMore]="cityHasMore()"
              [loadingMore]="cityLoadingMore()"
              [value]="cityFilter()"
              (valueChange)="cityFilter.set($event ?? null)"
              (searchChange)="citySearch.emit($event)"
              (scrolledToEnd)="cityScrolledToEnd.emit()"
              placeholder="Auswählen…"
            />
          </div>
        }
      </div>
    }

    <div class="masterdata-search-modal-searchrow">
      <soft-ds-search-input
        #modalSearchInput
        class="masterdata-search-modal-search"
        size="md"
        [value]="_value() ?? ''"
        [loading]="triggerLoading()"
        loadingPlacement="trailing"
        [placeholder]="placeholder()"
        (valueChange)="onInputChange($event)"
        (cleared)="onInputChange('')"
        (focusin)="onModalSearchFocusIn()"
        (keydown.arrowdown)="onArrowDown($event)"
        (keydown.arrowup)="onArrowUp($event)"
        (keydown.enter)="onEnterSelect($event)"
        (keydown.tab)="onTabSelect($event)"
      />
      @if (!mobileSheet()) {
        <soft-ds-tabs
          class="masterdata-search-modal-tabs"
          size="md"
          [tabs]="modalTabs()"
          [(value)]="modalTab"
        />
      }
      @if (canExpandDetail()) {
        <soft-ds-button
          variant="outline"
          size="sm"
          mode="iconOnly"
          class="masterdata-search-detail-expand"
          aria-label="Details anzeigen"
          (click)="expandDetail()"
        >
          <soft-ds-icon *softSlot="'leading'" name="fa-regular fa-sidebar-flip" />
        </soft-ds-button>
      }
      @if (!mobileSheet()) {
        @if (modalTab() === 'favorites' && favoriteEntities().length > 0) {
          <button
            type="button"
            class="masterdata-search-section-action ds-caption masterdata-search-modal-clear"
            (click)="clearFavorites()"
          >
            Alle löschen
          </button>
        } @else if (modalTab() === 'last-used' && sortedLastUsedEntities().length > 0) {
          <button
            type="button"
            class="masterdata-search-section-action ds-caption masterdata-search-modal-clear"
            (click)="clearLastUsed()"
          >
            Alle löschen
          </button>
        }
      }
    </div>

    @if (showAdvancedFilters() && !mobileSheet()) {
      <div class="masterdata-search-modal-status">
        <soft-ds-checkbox [(checked)]="showDeleted" labelName="Gesperrte anzeigen">
          Gesperrte anzeigen
        </soft-ds-checkbox>
        <soft-ds-checkbox [(checked)]="showInactive" labelName="Inaktive anzeigen">
          Inaktive anzeigen
        </soft-ds-checkbox>
      </div>
    }

    @if (sortedLastSearchResults().length > 0 && !mobileSheet()) {
      <div class="masterdata-search-modal-chips">
        @for (e of sortedLastSearchResults(); track e.key) {
          <soft-ds-chip
            variant="outline"
            class="masterdata-search-modal-chip"
            leadingIcon="fa-regular fa-clock"
            removable
            removeLabel="Suchanfrage entfernen"
            (click)="_value.set(e.body.text); modalTab.set('results'); search()"
            (remove)="deleteLastSearchResult(e.key)"
          >
            {{ e.body.text }}
          </soft-ds-chip>
        }
      </div>
    }
  </div>
</ng-template>

<ng-template #modalBody>
  <div
    class="masterdata-search-modal-layout"
    [class.has-detail]="detailComponent() ? showDetailPane() : detailSidebar()"
    [class.has-detail-entity]="!!detailComponent()"
  >
    <div
      #resultsRegion
      class="masterdata-search-modal-content"
      tabindex="-1"
      (scroll)="onModalResultsScroll($event)"
    >
      @if (hasColumns()) {
        <table
          class="masterdata-search-table"
          [class.is-empty]="
            activeModalEntities().length === 0 &&
            !(modalTab() === 'results' && searching())
          "
          #resultsListElement
        >
          <thead>
            <tr>
              @if (multiSelecting()) {
                <th class="masterdata-search-table-check-col">
                  <soft-ds-checkbox
                    labelName="Alle auswählen"
                    [checked]="allActiveChecked()"
                    [indeterminate]="someActiveChecked()"
                    (checkedChange)="toggleAllActive()"
                  />
                </th>
              }
              @if (bookmarksEnabled()) {
                <th class="masterdata-search-table-fav-col" aria-label="Favorit"></th>
              }
              @for (col of effectiveColumns(); track col.label) {
                <th
                  class="ds-body-2-semibold"
                  [class.masterdata-search-table-entity-col]="col.type === 'entity'"
                  [attr.data-align]="col.align ?? 'start'"
                >
                  {{ col.label }}
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @if (modalTab() === 'results' && searching()) {
              @for (row of [1, 2, 3, 4, 5, 6, 7, 8]; track row) {
                <tr class="masterdata-search-table-skeleton-row">
                  @if (multiSelecting()) {
                    <td class="masterdata-search-table-check-col">
                      <soft-ds-loading-skeleton width="16px" height="16px" radius="4px" />
                    </td>
                  }
                  @if (bookmarksEnabled()) {
                    <td class="masterdata-search-table-fav-col">
                      <soft-ds-loading-skeleton width="16px" height="16px" radius="4px" />
                    </td>
                  }
                  @for (col of effectiveColumns(); track col.label) {
                    <td
                      class="masterdata-search-table-cell"
                      [class.masterdata-search-table-entity-cell]="col.type === 'entity'"
                    >
                      @switch (col.type) {
                        @case ('entity') {
                          <div class="masterdata-search-cell-entity">
                            <soft-ds-loading-skeleton
                              width="32px"
                              height="32px"
                              radius="var(--ds-radius-sm)"
                            />
                            <div class="masterdata-search-skeleton-lines">
                              <soft-ds-loading-skeleton
                                width="70%"
                                height="14px"
                                radius="4px"
                              />
                              <soft-ds-loading-skeleton
                                width="45%"
                                height="12px"
                                radius="4px"
                              />
                            </div>
                          </div>
                        }
                        @case ('badge') {
                          <soft-ds-loading-skeleton
                            width="5rem"
                            height="20px"
                            radius="999px"
                          />
                        }
                        @default {
                          <soft-ds-loading-skeleton
                            width="60%"
                            height="14px"
                            radius="4px"
                          />
                        }
                      }
                    </td>
                  }
                </tr>
              }
            } @else if (modalTab() === 'results' && hasError()) {
              <tr>
                <td class="masterdata-search-table-message" [attr.colspan]="colspan()">
                  <soft-ds-empty-state
                    class="masterdata-search-empty"
                    variant="error"
                    size="sm"
                    [title]="errorTitle()"
                    [description]="errorMessage()"
                    actionLabel="Erneut versuchen"
                    (action)="retry.emit()"
                  />
                </td>
              </tr>
            } @else if (activeModalEntities().length === 0) {
              <tr>
                <td class="masterdata-search-table-message" [attr.colspan]="colspan()">
                  <soft-ds-empty-state
                    class="masterdata-search-empty"
                    size="sm"
                    icon="fa-regular fa-inbox"
                    [title]="modalEmptyTitle()"
                    [description]="modalEmptyDescription()"
                  />
                </td>
              </tr>
            } @else {
              @for (
                entity of activeModalEntities();
                track entityTrack(entity);
                let i = $index
              ) {
                <tr
                  class="masterdata-search-table-row"
                  [class.is-selected]="
                    !multiSelecting() &&
                    (usePreviewFlow()
                      ? previewObject() === entity
                      : selectedObject() === entity)
                  "
                  [class.is-checked]="multiSelecting() && isChecked(entity)"
                  [class.is-highlighted]="highlightedResultIndex() === i"
                  [attr.data-result-index]="i"
                  (click)="onTableRowClick(entity)"
                  (dblclick)="onTableRowDblClick(entity)"
                >
                  @if (multiSelecting()) {
                    <td class="masterdata-search-table-check-col">
                      <soft-ds-checkbox
                        [labelName]="
                          'Auswählen ' + columnTitle(effectiveColumns()[0], entity)
                        "
                        [checked]="isChecked(entity)"
                        (checkedChange)="toggleChecked(entity)"
                        (click)="$event.stopPropagation()"
                        (dblclick)="$event.stopPropagation()"
                      />
                    </td>
                  }
                  @if (bookmarksEnabled()) {
                    <td class="masterdata-search-table-fav-col">
                      <button
                        type="button"
                        class="masterdata-search-favorite"
                        [class.is-active]="isFavorite(entity)"
                        aria-label="Favorit"
                        (click)="setFavorite(entity); $event.stopPropagation()"
                        (dblclick)="$event.stopPropagation()"
                      >
                        <soft-ds-icon
                          [name]="
                            isFavorite(entity) ? 'fa-solid fa-star' : 'fa-regular fa-star'
                          "
                        />
                      </button>
                    </td>
                  }
                  @for (col of effectiveColumns(); track col.label) {
                    <td
                      class="masterdata-search-table-cell"
                      [class.masterdata-search-table-entity-cell]="col.type === 'entity'"
                      [attr.data-align]="col.align ?? 'start'"
                    >
                      @switch (col.type) {
                        @case ('entity') {
                          <div class="masterdata-search-cell-entity">
                            <span class="masterdata-search-media">
                              <soft-ds-icon [name]="col.icon ?? icon()" />
                            </span>
                            <div class="masterdata-search-cell-text">
                              <span
                                class="ds-body-2-semibold masterdata-search-cell-title"
                              >
                                {{ columnTitle(col, entity) }}
                              </span>
                              @if (columnSecondary(col, entity); as secondary) {
                                <span class="ds-caption color-light truncate">
                                  {{ secondary }}
                                </span>
                              }
                            </div>
                          </div>
                        }
                        @case ('badge') {
                          @if (columnBadge(col, entity); as badge) {
                            <soft-ds-badge [variant]="badgeVariant(badge)" size="sm">
                              {{ badge.label }}
                            </soft-ds-badge>
                          }
                        }
                        @default {
                          <span
                            class="ds-body-2"
                            [class.is-danger]="cellTone(col, entity) === 'danger'"
                          >
                            {{ cellValue(col, entity) }}
                          </span>
                        }
                      }
                    </td>
                  }
                </tr>
              }
            }
          </tbody>
        </table>
      } @else if (modalTab() === 'results' && searching()) {
        <div class="masterdata-search-modal-skeleton">
          <div class="masterdata-search-modal-skeleton-row" *softRepeat="8">
            <soft-ds-loading-skeleton height="16px" radius="4px" />
          </div>
        </div>
      } @else if (modalTab() === 'results' && hasError()) {
        <soft-ds-empty-state
          class="masterdata-search-empty"
          variant="error"
          size="sm"
          [title]="errorTitle()"
          [description]="errorMessage()"
          actionLabel="Erneut versuchen"
          (action)="retry.emit()"
        />
      } @else if (activeModalEntities().length === 0) {
        <soft-ds-empty-state
          class="masterdata-search-empty"
          size="sm"
          icon="fa-regular fa-inbox"
          [title]="modalEmptyTitle()"
          [description]="modalEmptyDescription()"
        />
      } @else {
        <div class="masterdata-search-list" #resultsListElement>
          @for (
            entity of activeModalEntities();
            track entityTrack(entity);
            let i = $index
          ) {
            <ng-container
              *ngTemplateOutlet="
                entityRow;
                context: {
                  $implicit: entity,
                  onSelect: selectResultFromModal,
                  index: i,
                }
              "
            />
          }
        </div>
      }
      @if (loadingMore() && modalTab() === 'results') {
        <div class="masterdata-search-list__more">
          <soft-ds-loading-spinner size="sm" />
        </div>
      }
    </div>

    @if (detailComponent(); as detailCmp) {
      @if (showDetailPane()) {
        <aside class="masterdata-search-modal-detail has-detail-component">
          <ng-container *ngComponentOutlet="detailCmp; inputs: detailComponentInputs()" />
        </aside>
      }
    } @else if (detailSidebar()) {
      <aside class="masterdata-search-modal-detail">
        @if (previewObject(); as preview) {
          <div class="masterdata-search-detail-head">
            <span class="masterdata-search-media">
              <soft-ds-icon [name]="icon()" />
            </span>
            <span class="ds-body-1-semibold masterdata-search-detail-title">
              {{ detailRows()[0]?.value ?? label() }}
            </span>
          </div>
          <dl class="masterdata-search-detail-list">
            @for (row of detailRows(); track row.label) {
              <div class="masterdata-search-detail-row">
                <dt class="ds-caption color-light">{{ row.label }}</dt>
                <dd class="ds-body-2">{{ row.value || '–' }}</dd>
              </div>
            }
          </dl>
        } @else {
          <div class="masterdata-search-detail-empty">
            <span class="masterdata-search-detail-empty-icon">
              <soft-ds-icon [name]="icon()" />
            </span>
            <span class="ds-body-1-semibold">{{ label() }} auswählen</span>
            <span class="ds-body-2 color-light masterdata-search-detail-empty-hint">
              Wählen Sie links einen Eintrag, um Details zu sehen.
            </span>
          </div>
        }
      </aside>
    }
  </div>
</ng-template>

<ng-template #modalFooter>
  <div class="masterdata-search-modal-footer" [class.is-sheet]="mobileSheet()">
    @if (multiSelecting()) {
      <span
        class="masterdata-search-modal-preview ds-body-2"
        [class.is-empty]="selectedObjects().length === 0"
      >
        @if (selectedObjects().length > 0) {
          <soft-ds-icon [name]="icon()" />
          {{ selectedObjects().length }} ausgewählt
        } @else {
          <soft-ds-icon name="fa-regular fa-arrow-pointer" />
          Kein {{ label() }} ausgewählt — Checkbox anklicken
        }
      </span>
    } @else if (usePreviewFlow()) {
      <span
        class="masterdata-search-modal-preview ds-body-2"
        [class.is-empty]="!previewObject()"
      >
        @if (previewObject(); as preview) {
          <soft-ds-icon [name]="icon()" />
          {{ displayWith()(preview) }}
        } @else {
          <soft-ds-icon name="fa-regular fa-arrow-pointer" />
          Kein {{ label() }} ausgewählt — Zeile anklicken
        }
      </span>
    }
    @if (isExpandedView() && detailComponent() && previewObject() && !mobileSheet()) {
      <span class="masterdata-search-modal-sidebar-hint">
        <soft-ds-kbd aria-hidden="true">Strg</soft-ds-kbd>
        <soft-ds-kbd aria-hidden="true">←</soft-ds-kbd>
        <span class="masterdata-search-modal-sidebar-hint-label">Details öffnen</span>
        <soft-ds-kbd aria-hidden="true">→</soft-ds-kbd>
        <span class="masterdata-search-modal-sidebar-hint-label">schließen</span>
      </span>
    }
    @if (multiToggle() && !multiple() && !mobileSheet()) {
      <soft-ds-button
        [variant]="multiSelectOverride() ? 'primary' : 'outline'"
        size="md"
        (click)="toggleMultiSelect()"
        data-testid="masterdata-multi-toggle"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-list-check" />
        </ng-template>
        Mehrfachauswahl
      </soft-ds-button>
    }
    <soft-ds-button variant="secondary" size="md" (click)="dismissModal()">
      Abbrechen
    </soft-ds-button>
    @if (multiSelecting()) {
      <soft-ds-button
        variant="primary"
        size="md"
        [disabled]="selectedObjects().length === 0"
        (click)="confirmMultiSelection()"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-check" />
        </ng-template>
        {{ selectedObjects().length }} übernehmen
      </soft-ds-button>
    } @else if (usePreviewFlow()) {
      <soft-ds-button
        variant="primary"
        size="md"
        [disabled]="!previewObject()"
        (click)="confirmPreview()"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-check" />
        </ng-template>
        {{ label() }} übernehmen
      </soft-ds-button>
    }
  </div>
</ng-template>

<ng-template
  #entityRow
  let-entity
  let-onSelect="onSelect"
  let-index="index"
  let-meta="meta"
>
  <soft-ds-item
    size="md"
    variant="default"
    [interactive]="true"
    [attr.data-result-index]="index ?? null"
    [class.is-highlighted]="index !== undefined && highlightedResultIndex() === index"
    [class.is-selected]="
      (multiSelecting() && isChecked(entity)) ||
      (mobileSheet() && previewObject() === entity)
    "
    (activate)="onSelect(entity)"
  >
    <soft-ds-item-leading>
      @if (multiSelecting()) {
        <soft-ds-checkbox
          class="masterdata-search-row-check"
          [labelName]="'Auswählen ' + displayWith()(entity)"
          [checked]="isChecked(entity)"
          (checkedChange)="toggleChecked(entity)"
          (click)="$event.stopPropagation()"
        />
      }
      @if (avatarOf(entity); as av) {
        @if (av.src) {
          <soft-ds-avatar size="md" [src]="av.src" [initials]="av.initials" />
        } @else {
          <span
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary"
          >
            {{ av.initials }}
          </span>
        }
      } @else {
        <span class="masterdata-search-media">
          <soft-ds-icon [name]="icon()" />
        </span>
      }
    </soft-ds-item-leading>
    <div class="masterdata-search-content">
      <ng-container
        *ngTemplateOutlet="
          resultTemplate();
          context: { $implicit: entity, searchTerm: _value() }
        "
      />
    </div>
    <soft-ds-item-trailing>
      @if (meta) {
        <span class="ds-caption masterdata-search-meta">{{ meta | date: 'short' }}</span>
      }
      @if (bookmarksEnabled()) {
        <button
          type="button"
          class="masterdata-search-favorite"
          [class.is-active]="isFavorite(entity)"
          aria-label="Favorit"
          (click)="setFavorite(entity); $event.stopPropagation()"
        >
          <soft-ds-icon
            [name]="isFavorite(entity) ? 'fa-solid fa-star' : 'fa-regular fa-star'"
          />
        </button>
      }
    </soft-ds-item-trailing>
  </soft-ds-item>
</ng-template>
